import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { track } from "@/lib/api";
import {
  ShieldCheck,
  Heart,
  Bell,
  CheckCircle,
  ArrowLeft,
} from "@phosphor-icons/react";

const pledgeAmounts = [10, 25, 50, 100];

export default function GivePage() {
  const [mode, setMode] = useState("monthly");
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [history, setHistory] = useState([]);

  const finalAmount = custom ? Number(custom) : amount;

  const loadHistory = () => api.get("/donations/me").then((r) => setHistory(r.data.items || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const handlePledge = async () => {
    if (!finalAmount || finalAmount <= 0) { toast.error("Choose an amount to pledge"); return; }
    setSubmitting(true);
    track("donation_started", { amount: finalAmount, mode, kind: "pledge" });
    try {
      const res = await api.post("/donations", {
        amount_usd: finalAmount,
        mode,
        method: "other",  // keep crypto-payment fields out of the pledge UX
        note: "Early supporters pledge — payments not yet live",
      });
      setConfirmation({ amount: finalAmount, mode, id: res.data.id });
      track("donation_completed", { amount: finalAmount, mode, kind: "pledge", id: res.data.id });
      loadHistory();
    } catch {
      toast.error("Couldn't save your pledge. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="px-5 md:px-8 py-6 max-w-lg mx-auto" data-testid="give-thanks">
        <div className="glass-card-strong p-8 text-center relative overflow-hidden">
          <div className="spiritual-orb-sm w-40 h-40 -top-10 -right-10 animate-orb" />
          <div className="relative z-10">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#FAF5EC] flex items-center justify-center mb-4">
              <CheckCircle size={32} weight="fill" className="text-[#C4A265]" />
            </div>
            <h1 className="font-heading text-3xl text-[#2D1B0E] font-medium mb-2">You're on the early supporters list</h1>
            <p className="font-body text-[#5C4033] text-sm mb-2">
              Your pledge of <b>${confirmation.amount}</b> {confirmation.mode === "monthly" ? "monthly" : "one-time"} is recorded.
            </p>
            <p className="font-body text-xs text-[#8B7355] mb-6 italic">
              "Each of you should give what you have decided in your heart to give." — 2 Corinthians 9:7
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 text-left">
              <div className="flex items-start gap-2">
                <Bell size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-900 font-body mb-0.5">We'll notify you the moment giving goes live.</p>
                  <p className="text-[11px] text-amber-800 font-body leading-relaxed">
                    Voice Global is finalizing payment partnerships. Until then, your pledge tells us you'd like to support the mission—no money is collected today.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => { setConfirmation(null); setCustom(""); setAmount(25); }}
              className="w-full py-3 rounded-full glass-card text-[#5C4033] font-body text-sm font-medium hover:bg-white/60"
              data-testid="give-another-btn"
            >
              <ArrowLeft size={14} className="inline mr-1" /> Update my pledge
            </button>
          </div>
        </div>
        {history.length > 0 && (
          <div className="mt-5 text-left" data-testid="give-history">
            <p className="text-[#8B7355] text-xs uppercase tracking-wider font-body mb-2">Your pledges</p>
            <div className="space-y-2">
              {history.slice(0, 5).map((d) => (
                <div key={d.id} className="glass-card p-3 flex items-center justify-between">
                  <div>
                    <p className="font-body text-sm text-[#2D1B0E] font-medium">${d.amount_usd}</p>
                    <p className="font-body text-[10px] text-[#8B7355]">{d.mode} · {new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-body">awaiting launch</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="px-5 md:px-8 py-4 max-w-lg mx-auto" data-testid="give-page">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-wider font-body font-medium mb-3">
          <Bell size={11} weight="fill" /> Giving launches soon
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] font-medium mb-2">Become an Early Supporter</h1>
        <p className="font-heading text-base text-[#8B7355] italic">Pledge today—we'll notify you when giving goes live.</p>
      </div>

      <div className="glass-card-strong p-5 mb-6 text-center" data-testid="give-mission">
        <Heart size={22} weight="fill" className="text-[#C4A265] mx-auto mb-2" />
        <p className="font-body text-sm text-[#5C4033] leading-relaxed">
          Your support empowers global discipleship—equipping leaders, planting churches, and walking alongside believers through prayer, learning, and community.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2" data-testid="give-disclaimer">
        <ShieldCheck size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-amber-900 font-body leading-relaxed">
          We're finalizing payment partnerships for direct giving. <b>No money is collected today.</b> Pledging tells us you'd like to give—we'll email you the moment it's live.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        {["monthly", "one-time"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
            className={`flex-1 py-2.5 rounded-full font-body text-sm capitalize transition-all ${mode === m ? "glass-card-strong text-[#5C4033] font-medium" : "text-[#8B7355] hover:bg-white/30"}`}>
            {m === "monthly" ? "Monthly pledge" : "One-time pledge"}
          </button>
        ))}
      </div>

      <div className="glass-card-strong p-5 mb-5" data-testid="amount-section">
        <p className="text-[10px] uppercase tracking-wider font-body text-[#8B7355] mb-3 text-center">Choose an amount</p>
        <div className="flex gap-2 justify-center mb-3 flex-wrap">
          {pledgeAmounts.map((a) => (
            <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`amount-${a}`}
              className={`px-5 py-2.5 rounded-full font-body text-sm transition-all ${amount === a && !custom ? "glass-card-strong text-[#5C4033] font-medium shadow-sm" : "text-[#8B7355] hover:bg-white/40"}`}>
              ${a}
            </button>
          ))}
          <input type="number" value={custom} onChange={(e) => { setCustom(e.target.value); setAmount(0); }}
            placeholder="Custom" data-testid="custom-amount"
            className="w-20 px-3 py-2 rounded-full bg-white/40 border border-[#E8DFD3] text-sm font-body text-center text-[#2D1B0E] placeholder:text-[#B8A088] focus:outline-none focus:ring-1 focus:ring-[#C4A265]" />
        </div>
      </div>

      <button
        onClick={handlePledge}
        disabled={submitting || !finalAmount}
        data-testid="pledge-btn"
        className="w-full py-3.5 rounded-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white font-body text-base font-medium transition-all mb-3 disabled:opacity-50 shadow-md inline-flex items-center justify-center gap-2"
      >
        <Bell size={16} weight="fill" />
        {submitting ? "Saving your pledge…" : `Notify me — pledge $${finalAmount || "—"} ${mode === "monthly" ? "monthly" : ""}`}
      </button>
      <p className="text-[10px] text-center text-[#B8A088] font-body">No payment is taken today.</p>

      {history.length > 0 && (
        <div className="mt-7" data-testid="give-history">
          <p className="text-[#8B7355] text-xs uppercase tracking-wider font-body mb-2">Your pledges</p>
          <div className="space-y-2">
            {history.slice(0, 5).map((d) => (
              <div key={d.id} className="glass-card p-3 flex items-center justify-between">
                <div>
                  <p className="font-body text-sm text-[#2D1B0E] font-medium">${d.amount_usd}</p>
                  <p className="font-body text-[10px] text-[#8B7355]">{d.mode} · {new Date(d.created_at).toLocaleDateString()}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-body">awaiting launch</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
